import { APPROVAL_MESSAGES, approvalCardPath, isApprovalInFlight } from '@fos/shared/contracts/approvals';
import type { BillingCandidateEntry, BillingDraftDto, BillingDraftItemDto } from '@fos/shared/contracts/billing';
import {
  BILLING_MARKED_ISSUED_NOTE,
  BILLING_PAYMENT_SOURCE_LABELS,
  BILLING_SIMULATION_NOTE,
  billingPeriodRangeText,
  KSEF_STATUS_LABELS,
  KSEF_STATUS_TONES,
} from '@fos/shared/labels';
import { toLocalDate } from '@fos/shared/time';
import {
  CalendarRange,
  Download,
  FileText,
  MoreHorizontal,
  Pencil,
  Plus,
  ReceiptText,
  RotateCcw,
  Send,
  SquarePen,
  Trash2,
  Wallet,
  X,
} from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { firstName, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../ui/Menu';
import { Mono } from '../../../ui/Mono';
import { Page } from '../../../ui/Page';
import { Breadcrumbs, PageHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { ModulePage, NotFoundPage } from '../../modules/pages/ModulePage';
import {
  billingExportUrl,
  clockText,
  dayMonthText,
  displayStatus,
  downloadBillingExport,
  fullDateText,
  hoursMinutesText,
  hoursRangeText,
  moneyAuto,
  moneyShort,
  moneyText,
  periodText,
  rateText,
  useBillingMode,
} from '../billing-format';
import {
  AddEntriesDialog,
  CancelDraftDialog,
  FixedLineDialog,
  MarkIssuedDialog,
  MarkPaidDialog,
  MarkSentDialog,
  PeriodDialog,
  ReopenPaidDialog,
  SettlementDetailsDialog,
  SubmitDialog,
} from '../components/DraftDialogs';

/**
 * Zestawienie (Figma 09 Billing Draft, 3033:4004): nagłówek „Klient · okres” ze statusem, linia meta (projekty,
 * stawka, opiekun), wpisy czasu z zaznaczaniem (roboczy: zaznaczone = w zestawieniu, odznaczone = niezafakturowane
 * z okresu), pozycje stałe, podsumowanie z kwotą netto i jedną akcją główną zależną od statusu:
 * roboczy → „Wyślij do wystawienia”, błąd → „Wyślij ponownie”, wystawiony → „Oznacz jako opłacone”.
 * Baner „Czeka na akceptację” prowadzi do /akceptacje; po wystawieniu numer faktury i oznaczenie symulacji.
 * Bez „Widzi stawki i kwoty” — same godziny.
 *
 * Rozliczenia z klientem (workspace bez systemu faktur, DECISIONS 2026-10-09): Szkic → „Oznacz jako wysłane
 * klientowi” → Wysłane do klienta (dalej edytowalne: wpisy, pozycje, kwoty, opis, numer, data) → „Oznacz jako
 * opłacone” → Opłacone („Otwórz do edycji” wraca do Wysłane); „Anuluj rozliczenie” zwalnia wpisy.
 */
export function BillingDraftPage() {
  const access = useAccess();
  const { id } = useParams();
  if (!access.can('billing')) return <ModulePage module="billing" />;
  if (!id) return <NotFoundPage />;
  return <DraftView id={id} />;
}

/** Odświeżanie zestawienia, gdy jego akceptacja jest zatwierdzona i czeka na wykonanie albo się wykonuje. */
const APPROVAL_STATE_POLL_MS = 2000;

function DraftView({ id }: { id: string }) {
  const query = trpc.billing.get.useQuery(
    { id },
    {
      refetchInterval: (q) =>
        isApprovalInFlight((q.state.data as BillingDraftDto | undefined)?.approval) ? APPROVAL_STATE_POLL_MS : false,
    },
  );
  const crumbs = [{ label: 'Rozliczenia', to: '/rozliczenia' }];
  if (query.isPending) {
    return (
      <Page>
        <div role="status" aria-label="Wczytywanie zestawienia">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="mt-4 h-7 w-80" />
          <Skeleton className="mt-2 h-4 w-64" />
          <div className="mt-8 grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <Skeleton className="h-72 rounded-xl" />
            <Skeleton className="h-56 rounded-xl" />
          </div>
        </div>
      </Page>
    );
  }
  if (query.isError) {
    return (
      <Page>
        <Breadcrumbs items={crumbs} />
        <Section className="mt-6">
          {appErrorCode(query.error) === 'NOT_FOUND' ? (
            <EmptyState
              icon={ReceiptText}
              title="Nie ma takiego zestawienia"
              description="Zestawienie mogło zostać usunięte albo nie masz dostępu do tego klienta."
            />
          ) : (
            <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
          )}
        </Section>
      </Page>
    );
  }
  return <DraftContent draft={query.data} fetching={query.isFetching} />;
}

type DialogKind =
  | 'line'
  | 'submit'
  | 'paid'
  | 'cancel'
  | 'period'
  | 'entries'
  | 'issued'
  | 'sent'
  | 'details'
  | 'reopenPaid';

/** Rozliczenie bez faktury z systemu faktur (oznaczone ręcznie, symulacja albo jeszcze niewystawione). */
function isSettlementDraft(draft: BillingDraftDto): boolean {
  const p = draft.invoice?.provider ?? null;
  return p === null || p === 'manual' || p === 'fake';
}

function DraftContent({ draft, fetching }: { draft: BillingDraftDto; fetching: boolean }) {
  const access = useAccess();
  const utils = trpc.useUtils();
  const mode = useBillingMode();
  const seesRates = access.flag('seesRates');
  const canPrepare = access.can('billing', 'edit') && access.flag('preparesBilling');
  const isDraft = draft.status === 'draft';
  // rozliczenie z klientem: wysłane zmienia osoba z „Wystawia faktury” (zasada w serwerze)
  const settlement = mode.settlement && isSettlementDraft(draft);
  const canSendNow = canPrepare && access.flag('issuesInvoices');
  const isSent = settlement && draft.status === 'issued' && !draft.paidAt;
  const isPaid = settlement && draft.status === 'issued' && Boolean(draft.paidAt);
  const editable = canPrepare && (isDraft || (isSent && canSendNow));
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [editItem, setEditItem] = useState<BillingDraftItemDto | null>(null);
  const close = (open: boolean) => {
    if (!open) {
      setDialog(null);
      setEditItem(null);
    }
  };
  const status = displayStatus(draft, mode.settlement);
  const period = periodText(draft);

  const canExport = access.flag('exportsData');
  const exportCsv = async () => {
    try {
      const name = await downloadBillingExport(billingExportUrl(draft.id, 'csv'), 'zestawienie.csv');
      toast.success(`Pobrano ${name}`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const reopen = trpc.billing.reopen.useMutation();
  const doReopen = async () => {
    try {
      await reopen.mutateAsync({ id: draft.id, version: draft.version });
      await utils.billing.invalidate();
      toast.success('Zestawienie wróciło do edycji');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const meta = [
    draft.projects.length > 0
      ? `${draft.projects.length === 1 ? 'Projekt' : 'Projekty'} ${draft.projects.join(', ')}`
      : null,
    seesRates && draft.rates && draft.rates.length === 1
      ? `${rateText(draft.rates[0] as string, draft.currency)} netto`
      : null,
    seesRates && draft.rates && draft.rates.length > 1 ? 'różne stawki' : null,
    draft.ownerName ? `opiekun ${firstName(draft.ownerName)}` : null,
  ].filter((v): v is string => Boolean(v));

  // nieznany wynik wystawienia: najpierw sprawdzenie w systemie fakturowym („Faktury nie ma”), potem reszta akcji
  const outcomeUnknown = issueOutcomeUnknown(draft);
  const canCancel =
    canPrepare &&
    !outcomeUnknown &&
    (draft.status === 'draft' ||
      draft.status === 'pending_approval' ||
      draft.status === 'error' ||
      (isSent && canSendNow));
  const canReopen = canPrepare && !outcomeUnknown && (draft.status === 'pending_approval' || draft.status === 'error');
  const menuItems: ReactNode[] = [];
  if (canExport) {
    menuItems.push(
      <MenuItem key="csv" icon={Download} onSelect={() => void exportCsv()}>
        Eksport CSV
      </MenuItem>,
    );
  }
  if (editable) {
    menuItems.push(
      <MenuItem key="period" icon={CalendarRange} onSelect={() => setDialog('period')}>
        Zmień okres
      </MenuItem>,
    );
  }
  if (settlement && canPrepare && (isDraft || ((isSent || isPaid) && canSendNow))) {
    menuItems.push(
      <MenuItem key="details" icon={SquarePen} onSelect={() => setDialog('details')}>
        {isPaid ? 'Numer faktury' : 'Szczegóły rozliczenia'}
      </MenuItem>,
    );
  }
  if (canReopen) {
    menuItems.push(
      <MenuItem key="reopen" icon={RotateCcw} onSelect={() => void doReopen()} disabled={reopen.isPending}>
        Wróć do edycji
      </MenuItem>,
    );
  }
  if (canCancel) {
    menuItems.push(
      <MenuItem key="cancel" icon={Trash2} tone="danger" onSelect={() => setDialog('cancel')}>
        {settlement ? 'Anuluj rozliczenie' : 'Anuluj zestawienie'}
      </MenuItem>,
    );
  }

  return (
    <Page>
      <PageHeader
        detail
        breadcrumbs={[{ label: 'Rozliczenia', to: '/rozliczenia' }]}
        title={`${draft.client.name} · ${period}`}
        aside={<StatusPill tone={status.tone}>{status.label}</StatusPill>}
        meta={
          <span className="flex flex-wrap items-center gap-x-1.5">
            <Mono className="text-caption text-fg-muted">{draft.label}</Mono>
            {meta.map((m) => (
              <span key={m}>· {m}</span>
            ))}
          </span>
        }
        actions={
          <>
            {canExport && (
              <Button asChild variant="secondary" className={TOUCH_BUTTON}>
                <a href={billingExportUrl(draft.id, 'pdf')} target="_blank" rel="noopener noreferrer">
                  <FileText size={16} strokeWidth={1.75} aria-hidden className="shrink-0" />
                  Podgląd PDF
                </a>
              </Button>
            )}
            {menuItems.length > 0 && (
              <Menu>
                <MenuTrigger asChild>
                  <IconButton icon={MoreHorizontal} label="Więcej akcji" size={32} className="max-md:size-11" />
                </MenuTrigger>
                <MenuContent align="end">{menuItems}</MenuContent>
              </Menu>
            )}
          </>
        }
      />
      <StatusBanner draft={draft} settlement={settlement} onMarkIssued={() => setDialog('issued')} />
      <div className="mt-6 grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* treść zestawienia (wpisy, pozycje stałe, uwagi) w jednej białej sekcji; podsumowanie obok jako karta */}
        <Section>
          <EntriesTable
            draft={draft}
            seesRates={seesRates}
            editable={editable}
            fetching={fetching}
            onAddOutside={() => setDialog('entries')}
          />
          <FixedItems
            draft={draft}
            seesRates={seesRates}
            editable={editable}
            onAdd={() => setDialog('line')}
            onEdit={(it) => {
              setEditItem(it);
              setDialog('line');
            }}
          />
          {draft.notes && (
            <p className="mt-6 rounded-lg bg-surface px-4 py-3 text-body-s whitespace-pre-line text-fg-secondary">
              {draft.notes}
            </p>
          )}
        </Section>
        <Summary
          draft={draft}
          seesRates={seesRates}
          canPrepare={canPrepare}
          settlement={settlement}
          modeReady={mode.ready}
          canSendNow={canSendNow}
          onSubmit={() => setDialog('submit')}
          onSent={() => setDialog('sent')}
          onPaid={() => setDialog('paid')}
          onReopenPaid={() => setDialog('reopenPaid')}
          onReopen={() => void doReopen()}
          reopening={reopen.isPending}
        />
      </div>
      <FixedLineDialog open={dialog === 'line'} onOpenChange={close} draft={draft} item={editItem} />
      <SubmitDialog open={dialog === 'submit'} onOpenChange={close} draft={draft} settlement={settlement} />
      <MarkPaidDialog open={dialog === 'paid'} onOpenChange={close} draft={draft} />
      <CancelDraftDialog open={dialog === 'cancel'} onOpenChange={close} draft={draft} settlement={settlement} />
      <MarkSentDialog open={dialog === 'sent'} onOpenChange={close} draft={draft} />
      <SettlementDetailsDialog open={dialog === 'details'} onOpenChange={close} draft={draft} />
      <ReopenPaidDialog open={dialog === 'reopenPaid'} onOpenChange={close} draft={draft} />
      <PeriodDialog open={dialog === 'period'} onOpenChange={close} draft={draft} />
      <AddEntriesDialog open={dialog === 'entries'} onOpenChange={close} draft={draft} />
      <MarkIssuedDialog open={dialog === 'issued'} onOpenChange={close} draft={draft} />
    </Page>
  );
}

/**
 * Stan zestawienia i jego akceptacji `billing.issue_invoice` (kompozycja Fali 3): czeka → zatwierdzona / wystawiana →
 * wystawiona (z oznaczeniem symulacji) albo błąd (z ręczną weryfikacją, gdy wynik nieznany); anulowane / wycofane.
 */
/** Wystawienie z nieznanym wynikiem (akceptacja `failed` + „Wymaga ręcznej weryfikacji”): faktura mogła powstać. */
function issueOutcomeUnknown(draft: BillingDraftDto): boolean {
  return (
    draft.status === 'pending_approval' &&
    draft.approval?.status === 'failed' &&
    draft.approval.manualCheckRequired === true
  );
}

/**
 * Ręczna weryfikacja wystawienia (tylko osoba z „Wystawia faktury” — ma dostęp do systemu fakturowego):
 * „Sprawdziłem: faktury nie ma” albo „Faktura jest — oznacz jako wystawione” (numer i data z systemu faktur).
 */
function IssueCheckActions({ draft, onMarkIssued }: { draft: BillingDraftDto; onMarkIssued: () => void }) {
  const access = useAccess();
  const utils = trpc.useUtils();
  const confirm = trpc.billing.confirmNotIssued.useMutation();
  if (!access.can('billing', 'edit') || !access.flag('issuesInvoices')) return null;
  const run = async () => {
    try {
      await confirm.mutateAsync({ id: draft.id, version: draft.version });
      await utils.billing.invalidate();
      toast.success('Zapisano: faktura nie powstała. Możesz wysłać zestawienie ponownie.');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      <Button
        variant="secondary"
        size="s"
        onClick={() => void run()}
        loading={confirm.isPending}
        className={TOUCH_BUTTON}
      >
        Sprawdziłem: faktury nie ma
      </Button>
      <Button variant="secondary" size="s" icon={ReceiptText} onClick={onMarkIssued} className={TOUCH_BUTTON}>
        Faktura jest — oznacz jako wystawione
      </Button>
    </div>
  );
}

function StatusBanner({
  draft,
  settlement,
  onMarkIssued,
}: {
  draft: BillingDraftDto;
  settlement: boolean;
  onMarkIssued: () => void;
}) {
  const access = useAccess();
  const cls = 'mt-6';
  const approval = draft.approval;
  const approvalLink = approval && access.can('approvals') ? approvalCardPath(approval.id) : undefined;
  switch (draft.status) {
    case 'draft':
      // akceptacja odrzucona, wygasła albo wycofana: zestawienie wróciło do edycji (adnotacja z `last_error`)
      return draft.lastError ? (
        <ContextBanner className={cls} tone="info">
          {draft.lastError} Wpisy są odblokowane — popraw zestawienie i wyślij je ponownie.
        </ContextBanner>
      ) : null;
    case 'pending_approval':
      if (issueOutcomeUnknown(draft)) {
        return (
          <ContextBanner className={cls} tone="warning" linkTo={approvalLink} linkLabel="Akceptacja">
            {APPROVAL_MESSAGES.manualCheck}: nie wiadomo, czy faktura powstała
            {approval?.error ? ` (${approval.error})` : ''}. Sprawdź w systemie fakturowym, zanim wyślesz zestawienie
            ponownie — inaczej mogą powstać dwie faktury.
            <IssueCheckActions draft={draft} onMarkIssued={onMarkIssued} />
          </ContextBanner>
        );
      }
      if (isApprovalInFlight(approval)) {
        return (
          <ContextBanner className={cls} tone="accent" linkTo={approvalLink} linkLabel="Przejdź">
            {settlement
              ? 'Zatwierdzone. Oznaczamy rozliczenie jako wysłane…'
              : 'Zatwierdzone. System fakturowy wystawia fakturę…'}
          </ContextBanner>
        );
      }
      if (settlement) {
        return (
          <ContextBanner className={cls} tone="accent" linkTo={approvalLink ?? '/akceptacje'} linkLabel="Przejdź">
            Czeka na akceptację. Po zatwierdzeniu przez osobę z uprawnieniem „Wystawia faktury” rozliczenie dostanie
            status „Wysłane do klienta”.
          </ContextBanner>
        );
      }
      return (
        <ContextBanner className={cls} tone="accent" linkTo={approvalLink ?? '/akceptacje'} linkLabel="Przejdź">
          Czeka na akceptację. Fakturę wystawi system fakturowy po zatwierdzeniu przez osobę z uprawnieniem „Wystawia
          faktury”.
        </ContextBanner>
      );
    case 'error':
      return (
        <ContextBanner className={cls} tone="warning" linkTo={approvalLink} linkLabel="Akceptacja">
          Wystawienie faktury nie powiodło się{draft.lastError ? `: ${draft.lastError}` : '.'}
          {approval?.manualCheckRequired ? ` ${APPROVAL_MESSAGES.manualCheck}: sprawdź w systemie fakturowym.` : ''}{' '}
          Wyślij ponownie, wróć do edycji albo anuluj zestawienie.
        </ContextBanner>
      );
    case 'issued':
      // rozliczenie z klientem: bez faktury, więc bez noty „Symulacja”
      if (settlement) return null;
      return draft.invoice?.simulated || approval?.simulated ? (
        <ContextBanner className={cls} tone="info" linkTo={approvalLink} linkLabel="Akceptacja">
          {BILLING_SIMULATION_NOTE}.
        </ContextBanner>
      ) : null;
    case 'cancelled':
      return (
        <ContextBanner className={cls} tone="info">
          {settlement ? 'Rozliczenie anulowane' : 'Zestawienie anulowane'}
          {draft.cancelledAt ? ` ${fullDateText(toLocalDate(new Date(draft.cancelledAt)))}` : ''}. Wpisy wróciły do{' '}
          {settlement ? 'nierozliczonych' : 'niezafakturowanych'}.
        </ContextBanner>
      );
    default:
      return null;
  }
}

interface Row {
  key: string;
  entryId: string;
  inDraft: boolean;
  date: string;
  description: string;
  startedAt: Date;
  endedAt: Date;
  seconds: number;
  amount?: string;
}

function rowFromItem(it: BillingDraftItemDto): Row | null {
  if (it.kind !== 'time' || !it.entry || !it.timeEntryId) return null;
  return {
    key: it.id,
    entryId: it.timeEntryId,
    inDraft: true,
    date: toLocalDate(new Date(it.entry.startedAt)),
    description: it.description,
    startedAt: it.entry.startedAt,
    endedAt: it.entry.endedAt,
    seconds: it.quantitySeconds ?? 0,
    ...(it.amount !== undefined ? { amount: it.amount } : {}),
  };
}

function rowFromCandidate(e: BillingCandidateEntry): Row {
  return {
    key: `c-${e.id}`,
    entryId: e.id,
    inDraft: false,
    date: e.date,
    description: e.description || 'Praca',
    startedAt: e.startedAt,
    endedAt: e.endedAt,
    seconds: e.durationSeconds,
    ...(e.amount !== undefined ? { amount: e.amount } : {}),
  };
}

/**
 * Wpisy czasu (Figma 09: „Wpisy czasu · 8 z 8”). W zestawieniu roboczym zaznaczone = w zestawieniu, odznaczone =
 * niezafakturowane wpisy klienta z okresu; zmiana od razu zapisuje (addEntries / removeEntries).
 */
function EntriesTable({
  draft,
  seesRates,
  editable,
  fetching,
  onAddOutside,
}: {
  draft: BillingDraftDto;
  seesRates: boolean;
  editable: boolean;
  fetching: boolean;
  onAddOutside: () => void;
}) {
  const utils = trpc.useUtils();
  const add = trpc.billing.addEntries.useMutation();
  const remove = trpc.billing.removeEntries.useMutation();
  const candidates = trpc.billing.candidates.useQuery(
    { clientId: draft.client.id, periodFrom: draft.periodFrom, periodTo: draft.periodTo },
    { enabled: editable },
  );
  const rows = useMemo<Row[]>(() => {
    const own = draft.items.map(rowFromItem).filter((r): r is Row => r !== null);
    const extra = editable ? (candidates.data?.groups ?? []).flatMap((g) => g.entries).map(rowFromCandidate) : [];
    return [...own, ...extra].sort((a, b) => (a.startedAt < b.startedAt ? 1 : a.startedAt > b.startedAt ? -1 : 0));
  }, [draft.items, candidates.data, editable]);
  const inDraft = rows.filter((r) => r.inDraft);
  const busy = add.isPending || remove.isPending || fetching || candidates.isFetching;
  const outside = editable ? (candidates.data?.outsidePeriod ?? 0) : 0;

  const change = async (ids: string[], on: boolean) => {
    if (ids.length === 0) return;
    try {
      const input = { draftId: draft.id, version: draft.version, timeEntryIds: ids };
      if (on) await add.mutateAsync(input);
      else await remove.mutateAsync(input);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      await utils.billing.invalidate();
    }
  };

  const title = editable ? `Wpisy czasu · ${inDraft.length} z ${rows.length}` : `Wpisy czasu · ${inDraft.length}`;
  const th = 'h-9 px-1 text-caption font-medium text-fg-muted';
  return (
    <section aria-label="Wpisy czasu">
      {rows.length === 0 ? (
        <div className="flex flex-col gap-3">
          <h2 className="text-caption font-medium text-fg-muted">Wpisy czasu</h2>
          <p className="rounded-lg bg-surface px-4 py-3 text-body-s text-fg-secondary">
            {editable && candidates.isPending ? 'Wczytywanie wpisów…' : 'Brak wpisów czasu w tym zestawieniu.'}
          </p>
        </div>
      ) : (
        <table className="w-full table-fixed border-collapse text-left text-body-s">
          <thead>
            <tr className="border-b border-line">
              <th scope="col" className={th}>
                <span className="flex items-center gap-3">
                  {editable && (
                    <Checkbox
                      checked={inDraft.length === rows.length}
                      disabled={busy}
                      onCheckedChange={(on) =>
                        void change(
                          rows.filter((r) => r.inDraft !== on).map((r) => r.entryId),
                          on,
                        )
                      }
                      label={<span className="sr-only">Zaznacz wszystkie</span>}
                      touchTarget
                    />
                  )}
                  {title}
                </span>
              </th>
              <th scope="col" className={cn(th, 'hidden w-[112px] sm:table-cell')}>
                Godziny
              </th>
              <th scope="col" className={cn(th, 'w-[52px] text-right')}>
                Czas
              </th>
              {seesRates && (
                <th scope="col" className={cn(th, 'w-[84px] text-right')}>
                  Kwota
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className={cn('border-b border-line', !r.inDraft && 'text-fg-muted')}>
                <td className="h-11 px-1 pr-3">
                  <span className="flex min-w-0 items-center gap-3">
                    {editable && (
                      <Checkbox
                        checked={r.inDraft}
                        disabled={busy}
                        onCheckedChange={(on) => void change([r.entryId], on)}
                        label={<span className="sr-only">{`${dayMonthText(r.date)} ${r.description}`}</span>}
                        touchTarget
                      />
                    )}
                    <Mono className="w-10 shrink-0 text-caption text-fg-muted">{dayMonthText(r.date)}</Mono>
                    <span
                      title={r.description}
                      className={cn('min-w-0 truncate', r.inDraft ? 'text-fg' : 'text-fg-muted')}
                    >
                      {r.description}
                    </span>
                  </span>
                </td>
                <td className="hidden px-1 sm:table-cell">
                  <Mono className="text-caption text-fg-secondary">{hoursRangeText(r.startedAt, r.endedAt)}</Mono>
                </td>
                <td className="px-1 text-right">
                  <Mono className={cn('text-caption', r.inDraft ? 'text-fg' : 'text-fg-muted')}>
                    {clockText(r.seconds)}
                  </Mono>
                </td>
                {seesRates && (
                  <td className="px-1 text-right">
                    <Mono className="text-caption text-fg-secondary">
                      {r.amount !== undefined ? moneyAuto(r.amount, draft.currency) : '—'}
                    </Mono>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editable && outside > 0 && (
        <Button variant="ghost" size="s" icon={Plus} className={`mt-2 ${TOUCH_BUTTON}`} onClick={onAddOutside}>
          Dodaj wpisy spoza okresu ({outside})
        </Button>
      )}
      {draft.entriesWithoutRate > 0 && seesRates && (
        <p className="mt-2 text-caption text-warning">
          {draft.entriesWithoutRate}{' '}
          {plural(draft.entriesWithoutRate, ['wpis nie ma', 'wpisy nie mają', 'wpisów nie ma'])} stawki — kwota 0 zł.
          Uzupełnij stawkę we wpisie albo w karcie klienta.
        </p>
      )}
    </section>
  );
}

function FixedItems({
  draft,
  seesRates,
  editable,
  onAdd,
  onEdit,
}: {
  draft: BillingDraftDto;
  seesRates: boolean;
  editable: boolean;
  onAdd: () => void;
  onEdit: (item: BillingDraftItemDto) => void;
}) {
  const utils = trpc.useUtils();
  const remove = trpc.billing.removeLine.useMutation();
  const items = draft.items.filter((i) => i.kind === 'fixed');
  const canAdd = editable && seesRates;
  if (items.length === 0 && !canAdd) return null;
  const drop = async (itemId: string) => {
    try {
      await remove.mutateAsync({ draftId: draft.id, version: draft.version, itemId });
      await utils.billing.invalidate();
      toast.success('Usunięto pozycję');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <section className="mt-8" aria-labelledby="billing-fixed-title">
      <div className="flex items-center justify-between gap-3 border-b border-line pb-2">
        <h2 id="billing-fixed-title" className="text-caption font-medium text-fg-muted">
          Pozycje stałe{items.length > 0 ? ` · ${items.length}` : ''}
        </h2>
        {canAdd && (
          <Button variant="ghost" size="s" icon={Plus} onClick={onAdd} className={TOUCH_BUTTON}>
            Dodaj pozycję
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="py-3 text-caption text-fg-muted">Np. hosting, domena albo konfiguracja — ilość × cena netto.</p>
      ) : (
        <ul>
          {items.map((it) => (
            <li key={it.id} className="flex min-h-11 items-center gap-3 border-b border-line py-1.5">
              <span className="min-w-0 flex-1 truncate text-body-s text-fg">{it.description}</span>
              {seesRates && it.unitPrice && (
                <Mono className="hidden text-caption text-fg-muted sm:inline">
                  {(it.quantity ?? '1').replace(/\.00$/, '').replace('.', ',')} ×{' '}
                  {moneyAuto(it.unitPrice, draft.currency)}
                </Mono>
              )}
              {seesRates && it.amount !== undefined && (
                <Mono className="w-[84px] text-right text-caption text-fg-secondary">
                  {moneyAuto(it.amount, draft.currency)}
                </Mono>
              )}
              {editable && seesRates && (
                <IconButton
                  icon={Pencil}
                  label={`Zmień pozycję: ${it.description}`}
                  size={28}
                  onClick={() => onEdit(it)}
                  className="max-md:size-11"
                />
              )}
              {editable && (
                <IconButton
                  icon={X}
                  label={`Usuń pozycję: ${it.description}`}
                  size={28}
                  onClick={() => void drop(it.id)}
                  disabled={remove.isPending}
                  className="max-md:size-11"
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="text-body-s text-fg-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right">{children}</dd>
    </div>
  );
}

/** Podsumowanie (Figma 09, panel po prawej): okres, czas, stawka, razem netto i akcja główna. */
function Summary({
  draft,
  seesRates,
  canPrepare,
  settlement,
  modeReady,
  canSendNow,
  onSubmit,
  onSent,
  onPaid,
  onReopenPaid,
  onReopen,
  reopening,
}: {
  draft: BillingDraftDto;
  seesRates: boolean;
  canPrepare: boolean;
  settlement: boolean;
  modeReady: boolean;
  canSendNow: boolean;
  onSubmit: () => void;
  onSent: () => void;
  onPaid: () => void;
  onReopenPaid: () => void;
  onReopen: () => void;
  reopening: boolean;
}) {
  const access = useAccess();
  // osoba z „Wystawia faktury”: „Wystaw fakturę” (jedno okno potwierdzenia), inni — do akceptacji
  const canIssueNow = access.can('approvals', 'edit') && access.flag('issuesInvoices') && seesRates;
  const rates = draft.rates ?? [];
  const rate =
    rates.length === 1
      ? rateText(rates[0] as string, draft.currency)
      : rates.length > 1
        ? `${moneyAuto(rates[0] as string, draft.currency)}–${rateText(rates[rates.length - 1] as string, draft.currency)}`
        : null;
  const fixedCount = draft.items.filter((i) => i.kind === 'fixed').length;
  const empty = draft.items.length === 0;

  let action: ReactNode = null;
  if (!modeReady && (draft.status === 'draft' || draft.status === 'error')) {
    action = null;
  } else if (settlement && canPrepare && (draft.status === 'draft' || draft.status === 'error')) {
    // rozliczenie z klientem: osoba z „Wystawia faktury” — od razu; inni — do akceptacji
    action = (
      <div className="flex flex-col gap-2">
        <Button
          variant="primary"
          icon={Send}
          fullWidth
          onClick={canSendNow ? onSent : onSubmit}
          disabled={empty}
          className="max-md:h-11"
        >
          {canSendNow ? 'Oznacz jako wysłane klientowi' : 'Wyślij do akceptacji'}
        </Button>
        {draft.status === 'error' && (
          <Button
            variant="secondary"
            icon={RotateCcw}
            fullWidth
            onClick={onReopen}
            loading={reopening}
            className="max-md:h-11"
          >
            Wróć do edycji
          </Button>
        )}
        <p className="mt-1 text-caption text-fg-muted">
          {empty
            ? 'Zaznacz wpisy albo dodaj pozycję, żeby wysłać rozliczenie.'
            : canSendNow
              ? 'Do opłacenia nadal możesz zmieniać pozycje i kwoty.'
              : 'Trafi do Akceptacji. Po zatwierdzeniu dostanie status „Wysłane do klienta”.'}
        </p>
      </div>
    );
  } else if (settlement && draft.status === 'issued' && draft.paidAt && canPrepare) {
    action = (
      <Button variant="secondary" icon={RotateCcw} fullWidth onClick={onReopenPaid} className="max-md:h-11">
        Otwórz do edycji
      </Button>
    );
  } else if (canPrepare && draft.status === 'draft') {
    action = (
      <>
        <Button variant="primary" icon={Send} fullWidth onClick={onSubmit} disabled={empty} className="max-md:h-11">
          {canIssueNow ? 'Wystaw fakturę' : 'Wyślij do wystawienia'}
        </Button>
        <p className="mt-3 text-caption text-fg-muted">
          {empty
            ? 'Zaznacz wpisy albo dodaj pozycję, żeby wysłać zestawienie.'
            : canIssueNow
              ? 'System fakturowy wystawi fakturę po potwierdzeniu, a te wpisy zostaną zablokowane.'
              : 'Trafi do Akceptacji. Po zatwierdzeniu system fakturowy wystawi fakturę, a te wpisy zostaną zablokowane.'}
        </p>
      </>
    );
  } else if (canPrepare && draft.status === 'error') {
    action = (
      <div className="flex flex-col gap-2">
        <Button variant="primary" icon={Send} fullWidth onClick={onSubmit} className="max-md:h-11">
          Wyślij ponownie
        </Button>
        <Button
          variant="secondary"
          icon={RotateCcw}
          fullWidth
          onClick={onReopen}
          loading={reopening}
          className="max-md:h-11"
        >
          Wróć do edycji
        </Button>
      </div>
    );
  } else if (draft.status === 'pending_approval') {
    action = <p className="text-caption text-fg-muted">Wpisy są zablokowane do czasu decyzji w Akceptacjach.</p>;
  } else if (draft.status === 'issued' && !draft.paidAt && canPrepare) {
    action = (
      <Button variant="primary" icon={Wallet} fullWidth onClick={onPaid} className="max-md:h-11">
        Oznacz jako opłacone
      </Button>
    );
  }

  return (
    <aside className="h-fit rounded-xl border border-line bg-panel p-4 md:p-6" aria-labelledby="billing-summary-title">
      <h2 id="billing-summary-title" className="text-body-s font-semibold text-fg">
        Podsumowanie
      </h2>
      <dl className="mt-3">
        <SummaryRow label="Okres">
          <Mono className="text-caption text-fg">{billingPeriodRangeText(draft.periodFrom, draft.periodTo)}</Mono>
        </SummaryRow>
        <SummaryRow label="Czas">
          <Mono className="text-caption text-fg">{hoursMinutesText(draft.totalSeconds)}</Mono>
        </SummaryRow>
        {seesRates && rate && (
          <SummaryRow label="Stawka">
            <Mono className="text-caption text-fg">{rate}</Mono>
          </SummaryRow>
        )}
        {seesRates && fixedCount > 0 && draft.fixedNet !== undefined && (
          <SummaryRow label="Pozycje stałe">
            <Mono className="text-caption text-fg">{moneyText(draft.fixedNet, draft.currency)}</Mono>
          </SummaryRow>
        )}
        {settlement && draft.invoiceNumber && (
          <SummaryRow label="Numer faktury">
            <Mono className="text-caption text-fg">{draft.invoiceNumber}</Mono>
          </SummaryRow>
        )}
        {settlement && draft.status === 'issued' && draft.invoice?.issuedAt && (
          <SummaryRow label="Wysłane">
            <Mono className="text-caption text-fg">{fullDateText(toLocalDate(new Date(draft.invoice.issuedAt)))}</Mono>
          </SummaryRow>
        )}
        {!settlement && draft.invoice?.number && (
          <SummaryRow label="Faktura">
            <Mono className="text-caption text-fg">{draft.invoice.number}</Mono>
          </SummaryRow>
        )}
        {draft.invoice?.dueDate && (
          <SummaryRow label="Termin">
            <Mono className="text-caption text-fg">{fullDateText(draft.invoice.dueDate)}</Mono>
          </SummaryRow>
        )}
        {draft.invoice?.ksef && (
          <SummaryRow label="KSeF">
            <StatusPill tone={KSEF_STATUS_TONES[draft.invoice.ksef.kind]} size="s">
              {KSEF_STATUS_LABELS[draft.invoice.ksef.kind]}
              {draft.invoice.ksef.demo ? ' (demo)' : ''}
            </StatusPill>
          </SummaryRow>
        )}
        {draft.paidAt && (
          <SummaryRow label={settlement ? 'Opłacone' : 'Opłacona'}>
            <Mono className="text-caption text-fg">{fullDateText(toLocalDate(new Date(draft.paidAt)))}</Mono>
            {draft.paymentSource === 'integration' && (
              <span className="block text-caption text-fg-muted">{BILLING_PAYMENT_SOURCE_LABELS.integration}</span>
            )}
          </SummaryRow>
        )}
      </dl>
      <div className="mt-3 flex items-baseline justify-between gap-4 border-t border-line pt-4">
        <span className="text-body-s font-semibold text-fg">{seesRates ? 'Razem netto' : 'Razem czas'}</span>
        <Mono className="text-h3 font-semibold text-fg">
          {seesRates && draft.totalNet !== undefined
            ? moneyShort(draft.totalNet, draft.currency)
            : clockText(draft.totalSeconds)}
        </Mono>
      </div>
      {draft.invoice?.ksef?.number && (
        <p className="mt-3 text-caption text-fg-muted">
          Numer KSeF: <Mono className="break-all text-fg">{draft.invoice.ksef.number}</Mono>
        </p>
      )}
      {draft.invoice?.ksef?.kind === 'error' && (
        <p className="mt-3 text-caption text-fg-muted">
          {draft.invoice.ksef.errors[0]
            ? `System faktur zgłasza: ${draft.invoice.ksef.errors[0]}. `
            : 'Faktura nie trafiła do KSeF. '}
          Popraw dane w systemie faktur i wyślij ją ponownie.
        </p>
      )}
      {draft.invoice?.markedManually && !settlement && (
        <p className="mt-3 text-caption text-fg-muted">{BILLING_MARKED_ISSUED_NOTE}.</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </aside>
  );
}
